import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { nostojs } from "@nosto/nosto-js";
import ky from "ky";

import { hooks } from "~/lib/ky-utils";
import type { NostoRequest, NostoResponse } from "~/api/nosto-category";

import { useSite } from "./useSite";

export const PAGE_SIZE = 100;

export function useNosto({
  category,
  filters,
  page = 1,
  sort,
}: {
  category: string;
  filters?: {
    field: string;
    value: string[];
  }[];
  page?: number;
  sort?: {
    field: string;
    order: string;
  }[];
}) {
  const site = useSite();

  const [sessionParams, setSessionParams] = useState<any>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    nostojs(async (api) => {
      const session = await api.getSearchSessionParams();
      setSessionParams(session);
    });
  }, []);

  const { data, isPending } = useQuery({
    queryKey: ["nosto-category", category, sessionParams, filters, page, sort],
    queryFn: async ({ signal }) => {
      const from = (page - 1) * PAGE_SIZE;

      const res = await ky
        .post("/api/nosto-category", {
          hooks,
          signal,
          headers: { "X-Site-Handle": site.handle },
          json: {
            category,
            filters,
            size: PAGE_SIZE,
            from,
            sort,
            sessionParams,
          } satisfies NostoRequest,
        })
        .json<NostoResponse>();

      return res;
    },
    enabled: !!category && !!sessionParams,
    staleTime: 1000 * 60, // Cache for 1 minute
    placeholderData: keepPreviousData,
  });

  return { data, isPending: !sessionParams || isPending };
}
